import React, { useCallback, useMemo } from 'react';

import type { SelectOption } from '@rtkwlf/fenrir-react';
import { Combobox, Primitive, size, Text } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import { useOrganizations } from '@apis/eloc/queries/use-organizations';
import { useOrganizationContext } from '@packages/legacy/organization-context';

type OrganizationDropdownProps = {
  organizationSwitchCallback?: () => void;
};

export const OrganizationDropdown: React.FC<OrganizationDropdownProps> = ({
  organizationSwitchCallback,
}) => {
  const { t } = useTranslation();
  const { selectOrganization, selectedOrganization } = useOrganizationContext();

  const {
    data: organizations,
    isPending: isPendingOrganizations,
    isError: isErrorOrganizations,
  } = useOrganizations();

  const [, setQuery] = useQueryParams({
    code: StringParam,
    page: StringParam,
    state: StringParam,
  });

  const items = useMemo(
    () =>
      organizations?.map((val) => ({
        text: val.name,
        value: val.id,
      })) ?? [],
    [organizations],
  );

  const handleOrgSelect = useCallback(
    (changes: SelectOption | null) => {
      const selected = organizations?.find((o) => o.id === changes?.value);
      if (selected) {
        selectOrganization(selected, organizationSwitchCallback);
        setQuery({ code: undefined, page: undefined, state: undefined });
      }
    },
    [organizations, selectOrganization, organizationSwitchCallback, setQuery],
  );

  if (
    !isPendingOrganizations &&
    !isErrorOrganizations &&
    organizations.length === 0
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <Primitive.div
      data-testid="organization-dropdown"
      maxWidth="30rem"
      css={{
        minWidth: size['16rem'],
      }}
    >
      {organizations?.length === 1 && (
        <Text truncate styledAs="body2">
          {selectedOrganization.name}
        </Text>
      )}
      {(organizations?.length ?? 0) > 1 && (
        <Combobox
          options={items}
          defaultText={t('organizationSwitch:emptyText')}
          filter="default"
          onSelection={handleOrgSelect}
          placeholder={t('organizationSwitch:selectOrganization')}
          isCreatable={false}
          isLoading={isPendingOrganizations}
          value={selectedOrganization.name}
        />
      )}
    </Primitive.div>
  );
};
